import { colors } from '@/constants';
import styled, { css } from 'styled-components';
import { RiCloseLine } from 'react-icons/ri';
import { MdOutlineDragIndicator } from 'react-icons/md';

interface Props {
  $isopen: boolean;
  $isOffline?: boolean;
  $fullHeight?: boolean;
  direction?: 'bottom' | 'right';
  $bottomMargin?: number;
  $showOverflow?: boolean | null;
  $stackLayer?: number;
}

const MODAL_BASE_Z_INDEX = 9999998;

const getModalZIndex = (stackLayer = 0, offset = 0) => MODAL_BASE_Z_INDEX + stackLayer * 2 + offset;

export const ModalContainer = styled.div`
  position: absolute;
`;

const modalEffects = (i: any) => {
  return css`
    ${i.direction === 'bottom' &&
    css`
      bottom: ${i.$isopen ? '0px' : '-110vh'};
    `}
    ${i.direction === 'right' &&
    css`
      bottom: 0px;
      left: ${i.$isopen ? '' : '155%'};
    `}
  `;
};

export const BgContainer = styled.div<Props>`
  left: 50%;
  width: 100%;
  /* Usar svh/dvh para cobrir a viewport visível no iOS */
  height: 100svh;
  @supports (height: 100dvh) {
    height: 100dvh;
  }
  z-index: ${({ $stackLayer = 0 }) => getModalZIndex($stackLayer)};
  position: fixed;
  transition: all 0.4s;
  max-width: var(--mobile);
  transform: translateX(-50%);
  background-color: ${colors.shadown1};
  opacity: ${({ $isopen }) => ($isopen ? 1 : 0)};
  ${(i) => modalEffects(i)};
`;

export const ModalContent = styled.div<Props>`
  z-index: ${({ $stackLayer = 0 }) => getModalZIndex($stackLayer, 1)};
  position: fixed;
  opacity: ${({ $isopen }) => ($isopen ? 1 : 0)};
  overflow: ${(i) => (i.$showOverflow ? 'visible' : 'hidden')};
  left: 50%;
  width: 100%;
  transform: translateX(-50%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-width: var(--mobile);
  background-color: ${colors.brand.white};
  box-shadow: 0px -3px 10px 0px ${colors.shadown3};
  /* Altura responsiva com safe-area, evitando underlap nas toolbars */
  max-height: calc(${(i) => (i.$fullHeight ? 100 : 75)}svh - ${({ $isOffline }) => ($isOffline ? 2 : 0)}px);
  @supports (height: 100dvh) {
    max-height: calc(${(i) => (i.$fullHeight ? 100 : 75)}dvh - ${({ $isOffline }) => ($isOffline ? 2 : 0)}px);
  }
  @supports not (height: 100svh) {
    max-height: calc(${(i) => (i.$fullHeight ? 100 : 75)}vh - ${({ $isOffline }) => ($isOffline ? 2 : 0)}px);
  }
  height: calc(${(i) => (i.$fullHeight ? '100svh' : 'auto')} - ${({ $isOffline }) => ($isOffline ? 2 : 0)}px);
  @supports (height: 100dvh) {
    height: calc(${(i) => (i.$fullHeight ? '100dvh' : 'auto')} - ${({ $isOffline }) => ($isOffline ? 2 : 0)}px);
  }

  border-top-left-radius: ${(i) => (i.$fullHeight ? 0 : 8)}px;
  border-top-right-radius: ${(i) => (i.$fullHeight ? 0 : 8)}px;
  touch-action: pan-y;
  user-select: none;
  will-change: transform;

  /* Espaço para home indicator / barra do Safari */
  /* padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px)); */

  ${(i) => modalEffects(i)}
  ${(props) =>
    props.$bottomMargin &&
    css`
      margin-bottom: ${props.$bottomMargin}px;
    `}
`;

export const DragHandle = styled.div`
  position: relative;
  z-index: 2;
  cursor: grab;
  user-select: none;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
`;

export const ModalDrag = styled.div`
  width: 30px;
  height: 100%;
  position: absolute;
  background-color: transparent;
  top: 0;
  right: 0px;
  position: absolute;
  display: inline-flex;
  justify-content: end;
  align-items: center;
  z-index: 2;
  cursor: grab;
  user-select: none;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
`;

export const DragIcon = styled(MdOutlineDragIndicator)`
  color: ${colors.gray.gray20};
  background-color: ${colors.brand.white};
  border-radius: 50%;
  margin-right: 2px;
`;

export const ModalBody = styled.div<{ $fullHeight?: boolean; $isScrollable?: boolean }>`
  width: 100%;
  overflow-y: ${(i) => (i.$isScrollable ? 'auto' : 'hidden')};
  overflow-x: hidden;

  /* Altura interna responsiva com safe-area */
  max-height: ${(i) => (i.$fullHeight ? 100 : 75)}svh;
  @supports (height: 100dvh) {
    max-height: ${(i) => (i.$fullHeight ? 100 : 75)}dvh;
  }
  @supports not (height: 100svh) {
    max-height: ${(i) => (i.$fullHeight ? 100 : 75)}vh;
  }

  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  overscroll-behavior: contain;
  touch-action: pan-y;
  position: relative;
  z-index: 1;

  /* Esconder barra no iOS/Chrome mobile e ainda permitir scroll */
  scrollbar-width: none;
  -ms-overflow-style: none;

  /* Garantir conteúdo acima da área segura */
  /* padding-bottom: max(16px, env(safe-area-inset-bottom, 0px)); */

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const Header = styled.div`
  width: 100%;
  z-index: 15;
  right: 0px;
  position: absolute;
  display: inline-flex;
  justify-content: end;
  padding: 8px;
  cursor: grab;
  user-select: none;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
`;

export const DragHandleContent = styled.div<{ $dragHeaderColor?: string }>`
  width: 100%;
  height: 4px;
  position: absolute;
  padding-top: 8px;
  padding-bottom: 8px;
  z-index: 2;
  top: 10px;
  left: 0;
  user-select: none;
  touch-action: none;
  display: grid;
  place-items: center;
  &:before {
    content: '';
    width: 40px;
    height: 4px;
    background-color: ${({ $dragHeaderColor }) => $dragHeaderColor || colors.gray.gray10};
    border-radius: 4px;
  }
`;

export const CloseIcon = styled(RiCloseLine)`
  margin-top: 8px;
  margin-right: 8px;
  color: ${colors.gray.gray40};
  background-color: ${colors.brand.white};
  border-radius: 50%;
  cursor: pointer;
  padding: 4px;
  box-shadow: 1px 1px 10px 1px rgba(45, 45, 45, 0.07);
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
    color: ${colors.gray.gray60};
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const HeaderIcon = styled.div`
  width: 220px;
  height: 220px;
  position: absolute;
  top: -110px;
  left: 50%;
  transform: translateX(-50%);
`;
